import { SafeError } from './SafeError';

export interface WorkerChannelRequest {
  cbId: string;
  type: string;
  payload: any;
}

export interface WorkerChannelResponse {
  cbId: string;
  payload: any;
  error: Error | null;
}

export interface WorkerChannelNotify {
  type: string;
  payload: any;
}

export interface WorkerChannelAsyncHandler {
  resolve: (payload?: any) => void;
  reject: (error: Error) => void;
}

export interface WorkerChannelHandlerEvent extends WorkerChannelAsyncHandler {
  payload: any;
}

export class WorkerChannelHandler extends EventTarget {
  constructor(private readonly self: typeof globalThis) {
    super();
    this.onmessage();
  }
  postMessage(msg: WorkerChannelResponse) {
    this.self.postMessage(msg);
  }
  onmessage() {
    this.self.onmessage = (e: MessageEvent<WorkerChannelRequest>) => {
      const { cbId, type, payload } = e.data;
      this.dispatchEvent(
        new MessageEvent<WorkerChannelHandlerEvent>(type, {
          data: {
            payload,
            resolve: (payload) => {
              this.postMessage({ cbId, payload, error: null });
            },
            reject: (error) => {
              this.postMessage({ cbId, payload: null, error: new SafeError(error) });
            },
          },
        })
      );
    };
  }
  notify(type: string, payload: any) {
    this.self.postMessage({ type, payload });
  }
}

export class WorkerChannel extends EventTarget {
  private readonly callbackMap: Record<string, WorkerChannelAsyncHandler> = {};
  private callbackId = 1;
  constructor(private readonly worker: Worker) {
    super();
    this.onmessage();
  }
  postMessage(msg: WorkerChannelRequest) {
    this.worker.postMessage(msg);
  }
  onmessage() {
    this.worker.onmessage = (e: MessageEvent<WorkerChannelNotify & WorkerChannelResponse>) => {
      const { type, cbId, payload, error } = e.data;
      if (cbId) {
        const { resolve, reject } = this.callbackMap[cbId];
        if (error) {
          reject(error);
        } else {
          resolve(payload);
        }
        delete this.callbackMap[cbId];
      } else {
        this.dispatchEvent(new MessageEvent(type, { data: payload }));
      }
    };
  }
  request<T = void>(type: string, payload?: any) {
    return new Promise<T>((resolve, reject) => {
      const cbId = `${type}_${this.callbackId++}`;
      this.callbackMap[cbId] = { resolve, reject };
      this.postMessage({ cbId, type, payload });
    });
  }
}
